import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react'

import { ACTIVE_TEAM_STORAGE_KEY, getPersistedActiveTeamId, getStoredActiveTeamId, normalizeTeamId, PERSONAL_WORKSPACE_ID } from '@/utils/workspace-scope'

type WorkspaceContextType = {
  activeTeamId: string | null
  selectedWorkspaceId: string
  setActiveTeamId: (teamId: string | null) => void
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

function getInitialTeamId(): string | null {
  return getStoredActiveTeamId() || getPersistedActiveTeamId()
}

export function WorkspaceContextProvider({ children }: { children: ReactNode }) {
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(getInitialTeamId)

  const setActiveTeamId = (teamId: string | null) => {
    const normalized = normalizeTeamId(teamId)
    setActiveTeamIdState(normalized)
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(ACTIVE_TEAM_STORAGE_KEY, normalized || PERSONAL_WORKSPACE_ID)
      } catch {
        // ignore storage issues
      }
    }
  }

  const value = useMemo<WorkspaceContextType>(
    () => ({
      activeTeamId,
      selectedWorkspaceId: activeTeamId || PERSONAL_WORKSPACE_ID,
      setActiveTeamId,
    }),
    [activeTeamId]
  )

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceContextProvider')
  }
  return context
}

export function useWorkspaceOptional() {
  return useContext(WorkspaceContext) ?? null
}
